import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Loader2, ArrowLeft, Pencil, ReceiptIndianRupee } from "lucide-react";
import api, { inr } from "./api";
import { useAuth } from "./AuthContext";

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4 py-2.5 border-b border-white/5">
    <span className="mono-label" style={{ color: "#64748B" }}>{label}</span>
    <span className="text-sm text-white text-right">{value || "—"}</span>
  </div>
);

const Card = ({ title, children }) => (
  <div className="bg-[#0C1322] border border-white/10 p-6">
    <h2 className="font-display font-bold text-lg text-white mb-4">{title}</h2>
    {children}
  </div>
);

export default function EmployeeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = ["super_admin", "hr_admin"].includes(user?.role);
  const [emp, setEmp] = useState(null);

  useEffect(() => {
    api.get(`/hr/employees/${id}`).then((r) => setEmp(r.data)).catch(() => {});
  }, [id]);

  if (!emp) return <div className="p-12 flex justify-center"><Loader2 className="animate-spin" style={{ color: "#00F0FF" }} /></div>;

  const { personal: p, employment: em, salary: s, bank: b, computed: c } = emp;

  return (
    <div data-testid="employee-detail-page">
      <Link to="/portal/employees" className="inline-flex items-center gap-2 mono-label mb-4 hover:text-cyan" style={{ color: "#64748B" }}>
        <ArrowLeft size={14} /> Back to employees
      </Link>

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <div className="font-mono-accent text-cyan text-sm" style={{ color: "#00F0FF" }}>{emp.employee_id}</div>
          <h1 className="font-display font-extrabold text-3xl md:text-4xl text-white tracking-tight mt-1">{p.full_name}</h1>
          <p className="text-sm mt-1" style={{ color: "#94A3B8" }}>{em.designation} · {em.department}</p>
        </div>
        <div className="flex gap-3">
          {["super_admin", "hr_admin", "finance"].includes(user?.role) && (
            <button onClick={() => navigate(`/portal/payroll?employee=${emp.employee_id}`)} data-testid="generate-payroll-btn"
              className="inline-flex items-center gap-2 bg-[#0066FF] hover:bg-[#00F0FF] text-white hover:text-[#060A14] px-5 py-3 mono-label transition-colors">
              <ReceiptIndianRupee size={16} /> Generate Payroll
            </button>
          )}
          {canEdit && (
            <button onClick={() => navigate(`/portal/employees/${emp.employee_id}/edit`)} data-testid="edit-employee-btn"
              className="inline-flex items-center gap-2 border border-white/15 text-white px-5 py-3 mono-label hover:border-white/40 transition-colors">
              <Pencil size={16} /> Edit
            </button>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <Card title="Personal Information">
          <Row label="Father's Name" value={p.fathers_name} />
          <Row label="Date of Birth" value={p.dob} />
          <Row label="Gender" value={p.gender} />
          <Row label="Marital Status" value={p.marital_status} />
          <Row label="Blood Group" value={p.blood_group} />
          <Row label="Mobile" value={p.mobile} />
          <Row label="Personal Email" value={p.personal_email} />
          <Row label="Official Email" value={p.official_email} />
          <Row label="Aadhaar" value={p.aadhaar} />
          <Row label="PAN" value={p.pan} />
          <Row label="Passport" value={p.passport} />
        </Card>

        <Card title="Employment Details">
          <Row label="Employee Code" value={em.employee_code} />
          <Row label="Date of Joining" value={em.date_of_joining} />
          <Row label="Employment Type" value={em.employment_type} />
          <Row label="Reporting Manager" value={em.reporting_manager} />
          <Row label="Office Location" value={em.office_location} />
          <Row label="Work Location" value={em.work_location} />
          <Row label="Status" value={em.employee_status} />
          <Row label="Probation Period" value={em.probation_period} />
          <Row label="UAN" value={em.uan} />
          <Row label="PF Number" value={em.pf_number} />
          <Row label="ESI Number" value={em.esi_number} />
        </Card>

        <Card title="Salary Structure (Monthly)">
          <Row label="Monthly CTC" value={inr(s.monthly_ctc)} />
          <Row label="Basic" value={inr(s.basic)} />
          <Row label="HRA" value={inr(s.hra)} />
          <Row label="Conveyance" value={inr(s.conveyance)} />
          <Row label="Special Allowance" value={inr(s.special_allowance)} />
          <Row label="Medical Allowance" value={inr(s.medical_allowance)} />
          <Row label="Employee PF" value={inr(s.employee_pf)} />
          <Row label="ESI" value={inr(s.esi)} />
          <Row label="Professional Tax" value={inr(s.professional_tax)} />
          <Row label="TDS" value={inr(s.tds)} />
          <Row label="Other Deductions" value={inr(s.other_deductions)} />
          <div className="mt-4 pt-4 grid grid-cols-3 gap-3">
            <div><div className="mono-label" style={{ color: "#64748B" }}>Gross</div><div className="text-white font-bold mt-1">{inr(c.gross_earnings)}</div></div>
            <div><div className="mono-label" style={{ color: "#64748B" }}>Deductions</div><div className="text-white font-bold mt-1">{inr(c.total_deductions)}</div></div>
            <div><div className="mono-label" style={{ color: "#64748B" }}>Net</div><div className="font-bold mt-1" style={{ color: "#00F0FF" }}>{inr(c.net_salary)}</div></div>
          </div>
        </Card>

        <Card title="Bank Details">
          <Row label="Bank Name" value={b.bank_name} />
          <Row label="Account Number" value={b.account_number} />
          <Row label="IFSC Code" value={b.ifsc} />
          <Row label="Branch" value={b.branch} />
          <Row label="UPI ID" value={b.upi} />
        </Card>
      </div>
    </div>
  );
}
